import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Globe, Eye, EyeOff, KeyRound } from 'lucide-react'
import { useUserStore } from '../../stores'
import { useI18n } from '../../i18n'
import { isSmsRefused, smsRefusedSentence, isSmsOptedOut } from '../../utils/smsRefusal'
import { authAPI } from '../../services/api'
import LanguageSelector from '../../components/LanguageSelector'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'
import OrellaPageHeader from '../../components/OrellaPageHeader'
import orellaLogo from '../../assets/logos/orella_46A8F9_logo.png'
import handIcon from '../../assets/hand_icon.webp'
// s95: Google/Apple sign-in lives in ONE place now (hooks/useSsoSignIn.js), shared with
// the Welcome screen; it also handles the 2FA code step for an SSO account.
import useSsoSignIn from '../../hooks/useSsoSignIn'
import SsoCodeSheet from '../../components/SsoCodeSheet'

/* ── Inline SVG brand logos ── */
const AppleLogo = () => (
  <svg width="17" height="20" viewBox="0 0 18 22" fill="currentColor">
    <path d="M17.05 15.36c-.4.92-.59 1.33-1.1 2.15-.72 1.14-1.73 2.56-2.98 2.57-1.12.02-1.4-.73-2.92-.72-1.51.01-1.82.74-2.94.72-1.25-.02-2.2-1.29-2.92-2.43C2.54 14.88 2.4 11.6 3.56 9.85c.82-1.25 2.12-1.98 3.33-1.98 1.24 0 2.01.74 3.03.74.99 0 1.59-.74 3.01-.74 1.08 0 2.23.59 3.05 1.6-2.68 1.47-2.24 5.3.07 6.39zM12.15 6.3c.56-.72.98-1.73.83-2.76-.91.06-1.97.64-2.59 1.39-.56.68-1.03 1.7-.85 2.69.99.03 2.02-.56 2.61-1.32z"/>
  </svg>
)
const GoogleLogo = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
)

/* ── Refreshed field style ── */
const fieldStyle = {
  height: 48, borderRadius: 12, border: '1.5px solid #CBD5E1',
  padding: '0 14px', color: '#15354F', backgroundColor: '#F8FAFC', fontSize: 14,
}
const fieldFocus = (e) => { e.target.style.borderColor = '#46A8F9'; e.target.style.backgroundColor = '#fff' }
const fieldBlur = (e) => { e.target.style.borderColor = '#CBD5E1'; e.target.style.backgroundColor = '#F8FAFC' }
const Label = ({ children }) => (
  <label className="block text-[13px] font-semibold mb-1.5" style={{ color: '#15354F' }}>
    {children}
  </label>
)

/* ── Google Client ID from env ── */

// PROD-1271: patient 2FA self-reset by e-mail link (copy approved by Alex).
//   A 404 from the request route means this server has the feature switched OFF (it answers the
//   unknown-path 404). The link is then hidden for the rest of this page load: a module flag in
//   memory, never written to any storage. Nothing is probed on load; only a tap asks.
let twoFactorEmailResetUnavailable = false
const EMAIL_RESET_RESEND_SECONDS = 120

export default function SignInScreen() {
  const navigate = useNavigate()
  const { login, loading, error, setPendingUser, clearError } = useUserStore()
  const setUser = useUserStore((s) => s.setUser)
  const { t, currentLanguage } = useI18n()
  const [showLanguageModal, setShowLanguageModal] = useState(false)
  // s95d: from the front door (/email) the email arrives in router state, already checked;
  //   it is shown locked with "Change", and Apple/Google (which live on Welcome) are hidden.
  const location = useLocation()
  const frontEmail = location.state?.fromFrontDoor ? (location.state.email || '') : ''
  const [email, setEmail] = useState(frontEmail)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [code, setCode] = useState('')
  const [localError, setLocalError] = useState(null)
  const [attemptsInfo, setAttemptsInfo] = useState(null)
  const [lockTimer, setLockTimer] = useState(null)
  const [contentReady, setContentReady] = useState(!!sessionStorage.getItem('signin_animated'))
  const sso = useSsoSignIn({ mode: 'signin' })
  const ssoLoading = sso.ssoLoading
  
  const [awaitingSmsCode, setAwaitingSmsCode] = useState(false)
  const [smsSending, setSmsSending] = useState(false)
  const [show2faCode, setShow2faCode] = useState(false)
  const [showBackupEntry, setShowBackupEntry] = useState(false)
  const [backupCode, setBackupCode] = useState('')
  const [showLegacyPin, setShowLegacyPin] = useState(false)
  const [pin, setPin] = useState(['', '', '', ''])
  const pinRefs = useRef([])
  const codeRef = useRef(null)
  const lockIntervalRef = useRef(null)
  // PROD-1271: the "check your e-mail" panel of the 2FA step. Only flags and a countdown live
  //   here; the request sends the email and password state above, and nothing else is kept.
  const [emailResetPanel, setEmailResetPanel] = useState(false)
  const [emailResetBusy, setEmailResetBusy] = useState(false)
  const [emailResetResendIn, setEmailResetResendIn] = useState(0)
  const [emailResetNotice, setEmailResetNotice] = useState(null)
  const [emailResetHidden, setEmailResetHidden] = useState(twoFactorEmailResetUnavailable)
  
  /* ── Load Google Identity Services SDK ── */
  // ★ v242.157.0 — USERSTORE-TRANSIENT-PERSISTED-1. ForgotPasswordScreen,
  //   ResetPasswordVerify and NewPasswordScreen have all cleared the store
  //   error on mount for a long time; this screen and SignUpScreen were the two
  //   that did not, which is how a failed attempt's message outlived its
  //   attempt. When most of a family follows a rule the exceptions are
  //   omissions, not decisions (ORELLA_RULES §1dq(b)).
  //   ★★ THE STORE NO LONGER PERSISTS `error`, SO THIS IS NOT WHAT FIXES THE
  //      REPORTED COLD-START BANNER — partialize/merge is. This fixes the
  //      IN-SESSION half: navigating away and back, where the store is live.
  useEffect(() => {
    clearError()
  }, [])

  const handleGoogleSignIn = sso.startGoogle
  const handleAppleSignIn = sso.startApple

  /* ── Auth logic (all flows unchanged) ── */
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLocalError(null); setAttemptsInfo(null)
    if (!email || !password) { setLocalError(t('errors2.pleaseEnterEmail')); return }
    if (showBackupEntry) { if (!backupCode) { setLocalError(t('errors2.pleaseEnterBackup')); return }; await handleBackupLogin(); return }
    if (awaitingSmsCode && code) { await handleLoginWithCode(); return }
    if (showLegacyPin) { const pinStr = pin.join(''); if (pinStr.length !== 4) { setLocalError(t('errors2.pleaseEnterPin4')); return }; await handleLoginWithPin(pinStr); return }
    if (show2faCode && code) { await handleLoginWithCode() } else { await handleInitialLogin() }
  }
  
  const handleInitialLogin = async () => {
    const result = await login(email, password, null, null)
    if (result.success) { navigate('/home', { replace: true }); return }
    if (result.locked) { startLockTimer(result.remainingSeconds); return }
    if (result.needsVerification) { setPendingUser(result.userId); navigate('/verify', { replace: true, state: { from: 'signin' } }); return }
    if (result.needs2faSetup) { setPendingUser(result.userId); navigate('/security', { replace: true, state: { userId: result.userId } }); return }
    if (result.needs2faCode) { setShow2faCode(true); if (result.method === 'sms') { await sendSmsCode() } else { setLocalError(null); setTimeout(() => codeRef.current?.focus(), 100) }; return }
    if (result.needsPin) { setShowLegacyPin(true); setTimeout(() => pinRefs.current[0]?.focus(), 100); return }
    if (result.attemptsRemaining !== undefined && result.attemptsRemaining <= 2) setAttemptsInfo(result.attemptsRemaining)
    setLocalError(result.error || t('signIn.loginFailed'))
  }
  
  const handleLoginWithCode = async () => {
    const result = await login(email, password, null, code)
    if (result.success) { navigate('/home', { replace: true }) }
    else if (result.locked) { startLockTimer(result.remainingSeconds) }
    else { if (result.attemptsRemaining !== undefined && result.attemptsRemaining <= 2) setAttemptsInfo(result.attemptsRemaining); setLocalError(result.error || t('signIn.invalidVerificationCode')); setCode(''); codeRef.current?.focus() }
  }
  
  const handleLoginWithPin = async (pinStr) => {
    const result = await login(email, password, pinStr, null)
    if (result.success) { result.needs2faSetup ? navigate('/2fa-upgrade', { replace: true }) : navigate('/home', { replace: true }) }
    else if (result.locked) { startLockTimer(result.remainingSeconds) }
    else { if (result.attemptsRemaining !== undefined && result.attemptsRemaining <= 2) setAttemptsInfo(result.attemptsRemaining); setLocalError(result.error || t('signIn.incorrectPin')); setPin(['', '', '', '']); pinRefs.current[0]?.focus() }
  }
  
  const handleBackupLogin = async () => {
    try {
      const response = await authAPI.backupCodeLogin(email, password, backupCode)
      // PROD-943b: a backup-code sign-in is an e-mail/password session.
      if (response.token && response.user) { useUserStore.getState().setUser({ ...response.user, signInProvider: 'email' }, { freshSignIn: true }); response.needs2faResetup ? navigate('/security', { replace: true, state: { userId: response.user.id, resetup: true } }) : navigate('/home', { replace: true }) }
    } catch (err) { if (err.locked) { startLockTimer(err.remainingSeconds) } else { setLocalError(err.error || t('signIn.invalidBackupCode')); setBackupCode('') } }
  }
  
  const sendSmsCode = async () => {
    setSmsSending(true)
    try {
      const sent = await authAPI.send2faSms(email, password)
      // PROD-1166: a FAILED send to an unsubscribed number says why (answer key smsOptedOut: true); nothing went out.
      if (sent?.smsSent === false && isSmsOptedOut(sent)) { setLocalError(t('errors2.smsOptedOutSignIn')); return }
      setAwaitingSmsCode(true); setLocalError(null); setTimeout(() => codeRef.current?.focus(), 100)
    }
    catch (err) { setLocalError(isSmsOptedOut(err) ? t('errors2.smsOptedOutSignIn') : isSmsRefused(err) ? (smsRefusedSentence(err) || t('errors2.failedToSendSMS')) : (err.error || t('errors2.failedToSendSMS'))) }
    finally { setSmsSending(false) }
  }
  
  // PROD-1271: the "Send again" countdown. This effect only counts down; it sends nothing.
  useEffect(() => {
    if (emailResetResendIn <= 0) return undefined
    const handle = setTimeout(() => setEmailResetResendIn((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearTimeout(handle)
  }, [emailResetResendIn])

  // PROD-1271: ask for the 2FA reset link. The body is the email and password this step already
  //   holds (the send2faSms / backupCodeLogin shape). Every 2xx shows the same panel: the server
  //   answers one sentence for every case, so the body is never read. A 404 is "switched off".
  const requestEmailReset = async () => {
    if (emailResetBusy || lockTimer) return
    setEmailResetBusy(true); setEmailResetNotice(null); setLocalError(null)
    try {
      await authAPI.request2faEmailReset(email, password)
      setEmailResetPanel(true)
      setEmailResetResendIn(EMAIL_RESET_RESEND_SECONDS)
    } catch (err) {
      const status = err && err.status
      if (status === 404) {
        twoFactorEmailResetUnavailable = true
        setEmailResetHidden(true)
        setEmailResetPanel(false)
      } else if (status === 429) {
        setEmailResetNotice(t('twoFactorEmailReset.rateLimited'))
      } else {
        setEmailResetNotice(t('twoFactorEmailReset.requestFailed'))
      }
    } finally {
      setEmailResetBusy(false)
    }
  }
  const leaveEmailResetPanel = () => {
    setEmailResetPanel(false); setEmailResetNotice(null); setEmailResetResendIn(0)
    setShow2faCode(false); setShowBackupEntry(false); setAwaitingSmsCode(false)
    setCode(''); setBackupCode(''); setLocalError(null)
  }

  const startLockTimer = (seconds) => {
    setLockTimer(seconds); setLocalError(null)
    if (lockIntervalRef.current) clearInterval(lockIntervalRef.current)
    const interval = setInterval(() => { setLockTimer(prev => { if (prev <= 1) { clearInterval(interval); if (lockIntervalRef.current === interval) lockIntervalRef.current = null; return null } return prev - 1 }) }, 1000)
    lockIntervalRef.current = interval
  }
  // PROD-1271 (L2): the lock interval is cleared on unmount.
  useEffect(() => () => { if (lockIntervalRef.current) clearInterval(lockIntervalRef.current) }, [])
  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return
    const newPin = [...pin]; newPin[index] = value.slice(-1); setPin(newPin); setLocalError(null)
    if (value && index < 3) pinRefs.current[index + 1]?.focus()
    else if (value && index === 3) setTimeout(() => handleLoginWithPin(newPin.join('')), 200)
  }
  const handlePinKeyDown = (index, e) => { if (e.key === 'Backspace' && !pin[index] && index > 0) pinRefs.current[index - 1]?.focus() }
  const formatLockTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

  const reveal = (delay = 0) => ({
    opacity: contentReady ? 1 : 0,
    transform: contentReady ? 'translateY(0)' : 'translateY(12px)',
    transition: `opacity 0.4s ease ${delay}s, transform 0.4s ease ${delay}s`,
  })
  
  return (
    <ResponsiveWrapper className="bg-white" bottomInset={false}>
    <div className="screen-min-h bg-white flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 pt-8 pb-1">
        <img src={orellaLogo} alt="Orella" className="h-7 object-contain" />
        <button onClick={() => setShowLanguageModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 border rounded-full transition-colors"
          style={{ borderColor: '#E2E8F0', color: '#64748B' }}>
          <Globe size={13} className="text-orella-500" /><span className="text-xs font-bold text-orella-700">{currentLanguage.nativeName}</span>
        </button>
      </div>
      
      {/* Welcome Title */}
      <div className="px-5 pt-2 pb-2">
        <h2 className="text-2xl font-bold text-orella-700 flex items-center gap-2">
          {t('auth.welcome')}
          <img src={handIcon} alt="wave" className="w-7 h-7 object-contain wave-hand" />
        </h2>
      </div>

      <OrellaPageHeader
        message={t('signIn.signInToContinue')}
        sessionKey="signin"
        skipAnimation={!!sessionStorage.getItem('signin_animated')}
        chips={[]}
        onAnimationDone={() => { setContentReady(true); sessionStorage.setItem('signin_animated', 'true') }}
      />
      
      <LanguageSelector isOpen={showLanguageModal} onClose={() => setShowLanguageModal(false)} />
      
      {/* SSO Buttons */}
      {!frontEmail && (<>
      <div style={reveal(0)} className="px-6 pt-2">
        <button onClick={handleAppleSignIn} disabled={!!ssoLoading}
          className={`w-full flex items-center justify-center gap-2.5 font-semibold text-[15px] rounded-2xl active:scale-[0.98] transition-all disabled:opacity-60`}
          style={{ height: 52, background: '#000', color: '#fff', border: 'none' }}>
          <AppleLogo /> {ssoLoading === 'apple' ? t('signIn.signingIn') : t('signIn.continueWithApple')}
        </button>
        <button onClick={handleGoogleSignIn} disabled={!!ssoLoading}
          className={`w-full flex items-center justify-center gap-2.5 font-semibold text-[15px] rounded-2xl active:scale-[0.98] transition-all mt-2.5 disabled:opacity-60`}
          style={{ height: 52, background: '#fff', color: '#15354F', border: '1.5px solid #E2E8F0' }}>
          <GoogleLogo /> {ssoLoading === 'google' ? t('signIn.signingIn') : t('signIn.continueWithGoogle')}
        </button>
      </div>

      {/* Divider */}
      <div style={reveal(0.05)} className="flex items-center gap-4 px-6 py-4">
        <div className="flex-1 h-px" style={{ background: '#E2E8F0' }} />
        <span className="text-[10px] font-bold uppercase" style={{ color: '#15354F', letterSpacing: '0.8px' }}>{t('signIn.orSignInWithEmail')}</span>
        <div className="flex-1 h-px" style={{ background: '#E2E8F0' }} />
      </div>
      </>)}

      {/* Alerts */}
      <div style={reveal(0.1)} className="px-6">
        {lockTimer && (
          <div className="mb-3 p-3.5 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-700 font-medium text-sm">{t('signIn.accountTemporarilyLocked')}</p>
            {/* ★★★ THE LAST ONE, AND IT HAS BOTH SHAPES AT ONCE: a value in the middle and a
                LINK in the middle. The time is an ordinary param; the link is a placeholder
                left unresolved and split on, so Spanish decides where the clickable words go.
                ★★ `reset your password` was hardcoded too and the sweep could not see it —
                the arm requires a run to START with a capitalised word, and widening that to
                lowercase would flood it with fragments. Fixed here by hand, and the limit is
                recorded in scripts/lib/jsxText.mjs rather than papered over. */}
            <p className="text-red-600 text-sm mt-1">
              {t('signIn.tooManyAttempts', { time: formatLockTime(lockTimer), defaultValue: 'Too many failed attempts. Try again in {{time}} or {{reset}} to unlock immediately.' })
                .split('{{reset}}')
                .flatMap((part, i) => i === 0 ? [part] : [
                  <button key={`r${i}`} onClick={() => navigate('/forgot-password')} className="underline font-medium">{t('signIn.resetYourPassword', 'reset your password')}</button>,
                  part,
                ])}
            </p>
          </div>
        )}
        {!lockTimer && (localError || sso.ssoError || error) && (
          <div className="mb-3 p-3 rounded-xl text-sm bg-red-50 border border-red-200 text-red-600">
            {localError || sso.ssoError || error}
            {attemptsInfo !== null && attemptsInfo <= 2 && (
              <span className="block mt-1 text-red-500 font-medium">
                {attemptsInfo === 0 ? t('signIn.accountLockedWarning') : `${attemptsInfo} attempt${attemptsInfo !== 1 ? 's' : ''} remaining.`}
              </span>
            )}
          </div>
        )}
        {awaitingSmsCode && !localError && (
          <div className="mb-3 p-3 bg-orella-50 border border-orella-100 rounded-xl text-orella-700 text-sm">
            {t('signIn.verificationCodeSent', 'Verification code sent to your phone. Enter it below.')}
          </div>
        )}
      </div>
      
      {/* Email / Password form. PROD-1194: it fills the column so Sign In is pinned at the bottom. */}
      <div style={reveal(0.1)} className="flex-1 flex flex-col">
        <form onSubmit={handleSubmit} className="px-6 flex-1 flex flex-col">
          <div className="mb-3">
            <Label>{t('auth.email')}</Label>
            <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setLocalError(null); clearError() }}
              className="w-full focus:outline-none transition-colors" placeholder={t('auth.emailPlaceholder')}
              style={{ ...fieldStyle, ...(frontEmail ? { backgroundColor: '#F8FAFC' } : {}) }} onFocus={fieldFocus} onBlur={fieldBlur} required disabled={!!lockTimer}
              readOnly={!!frontEmail} />
            {frontEmail && (
              <button type="button" onClick={() => navigate('/email', { state: { email } })} className="text-xs font-semibold mt-1.5" style={{ color: '#46A8F9' }}>
                {t('emailStart.change', 'Use a different email')}
              </button>
            )}
          </div>
          <div className="mb-1">
            <Label>{t('auth.password')}</Label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => { setPassword(e.target.value); setLocalError(null); clearError() }}
                className="w-full focus:outline-none transition-colors" placeholder={t('auth.passwordPlaceholder')}
                style={{ ...fieldStyle, paddingRight: 44 }} onFocus={fieldFocus} onBlur={fieldBlur} required disabled={!!lockTimer} />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2" style={{ color: '#94A3B8' }} tabIndex={-1}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="text-right mb-3">
            <button type="button" onClick={() => navigate('/forgot-password')} className="text-xs font-semibold" style={{ color: '#46A8F9' }}>
              {t('auth.forgotPassword')}
            </button>
          </div>
          
          {show2faCode && !showLegacyPin && !showBackupEntry && !emailResetPanel && (
            <div className="mb-3">
              <Label>{awaitingSmsCode ? t('signIn.smsVerificationCode') : t('signIn.verificationCode')}</Label>
              <div className="relative">
                <input ref={codeRef} type="text" inputMode="numeric" autoComplete="one-time-code"
                  value={code} onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setLocalError(null) }}
                  className="w-full focus:outline-none transition-colors" placeholder={t('signIn.sixDigitCode')} maxLength={6}
                  style={{ ...fieldStyle, paddingRight: 44 }} onFocus={fieldFocus} onBlur={fieldBlur} disabled={!!lockTimer} />
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2" style={{ color: '#94A3B8' }}><KeyRound size={18} /></div>
              </div>
              <p className="text-[10px] mt-1.5" style={{ color: '#94A3B8' }}>
                {awaitingSmsCode ? t('signIn.enterCodeTexted') : t('signIn.fromAuthenticatorApp')}
              </p>
            </div>
          )}
          
          {showLegacyPin && (
            <div className="mb-3">
              <Label>{t('signIn.enterPinLabel')}</Label>
              <div className="flex justify-center gap-3 mt-2">
                {pin.map((digit, index) => (
                  <input key={index} ref={el => pinRefs.current[index] = el}
                    type="password" inputMode="numeric" maxLength={1} value={digit}
                    onChange={e => handlePinChange(index, e.target.value)} onKeyDown={e => handlePinKeyDown(index, e)}
                    className="w-14 h-16 text-center text-2xl font-semibold rounded-[14px] focus:outline-none transition-all"
                    style={{ backgroundColor: digit ? 'white' : '#E9F8FF', border: digit ? '2px solid #46A8F9' : '1.5px solid rgba(167,217,252,0.4)', color: '#15354F' }}
                    disabled={!!lockTimer} />
                ))}
              </div>
            </div>
          )}
          
          {showBackupEntry && !emailResetPanel && (
            <div className="mb-3">
              <Label>{t('signIn.backupCodeLabel')}</Label>
              <input type="text" value={backupCode} onChange={(e) => { setBackupCode(e.target.value.toLowerCase().trim()); setLocalError(null) }}
                className="w-full font-mono focus:outline-none transition-colors" placeholder={t('signIn.enterBackupCode')} maxLength={8}
                style={fieldStyle} onFocus={fieldFocus} onBlur={fieldBlur} disabled={!!lockTimer} />
              <button type="button" onClick={() => { setShowBackupEntry(false); setBackupCode(''); setLocalError(null) }}
                className="text-[10px] mt-1.5" style={{ color: '#94A3B8' }}>{t('signIn.backToVerificationCode')}</button>
            </div>
          )}
          
          {show2faCode && !showBackupEntry && !showLegacyPin && !emailResetPanel && (
            <div className="text-right mb-2">
              <button type="button" onClick={() => { setShowBackupEntry(true); setCode(''); setLocalError(null) }}
                className="text-xs" style={{ color: '#94A3B8' }}>{t('signIn.lostAuthenticator')}</button>
            </div>
          )}

          {/* PROD-1271 (Alex-approved copy): the e-mail reset link, on the 2FA step of the
              e-mail/password sign-in only (code view and backup-code view; never the password step,
              never the legacy PIN). While the lock timer runs it is greyed out (disabled, aria-disabled) with a
              line saying when it works; it is enabled again when the timer ends. */}
          {show2faCode && !showLegacyPin && !emailResetPanel && !emailResetHidden && (
            <div className="text-right mb-2">
              <button type="button" onClick={requestEmailReset} disabled={emailResetBusy || !!lockTimer} aria-disabled={emailResetBusy || !!lockTimer} aria-describedby={lockTimer ? 'twoFactorEmailResetLockedHint' : undefined}
                className="text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed" style={{ color: lockTimer ? '#94A3B8' : '#46A8F9' }}>{t('twoFactorEmailReset.link')}</button>
              <div role="status">
              {lockTimer && (
                <p id="twoFactorEmailResetLockedHint" className="text-xs mt-1.5" style={{ color: '#64748B' }}>{t('twoFactorEmailReset.lockedHint')}</p>
              )}
              </div>
              {emailResetNotice && (
                <p className="text-xs mt-1.5 text-red-600">{emailResetNotice}</p>
              )}
            </div>
          )}

          {/* PROD-1271: the server has the reset switched OFF (a 404): the support line instead. */}
          {show2faCode && !showLegacyPin && emailResetHidden && (
            <p className="text-xs text-right mb-2" style={{ color: '#64748B' }}>{t('twoFactorEmailReset.notAvailable')}</p>
          )}

          {/* PROD-1271 (copy approved by Alex, Q2): the panel that replaces the code field. The text
              says "If ...": it is shown for every 2xx, including the cases where nothing was sent. */}
          {show2faCode && emailResetPanel && (
            <div className="mb-3 p-4 bg-orella-50 border border-orella-100 rounded-xl" data-2fa-email-reset-panel="">
              <p className="font-semibold text-sm text-orella-700">{t('twoFactorEmailReset.panelTitle')}</p>
              <p className="text-sm mt-1.5" style={{ color: '#15354F' }}>{t('twoFactorEmailReset.panelText')}</p>
              {emailResetNotice && (
                <p className="text-xs mt-2 text-red-600">{emailResetNotice}</p>
              )}
              <div className="flex flex-col gap-2 mt-3">
                <button type="button" onClick={requestEmailReset} disabled={emailResetBusy || emailResetResendIn > 0}
                  className="w-full font-semibold text-sm rounded-full disabled:opacity-50"
                  style={{ height: 44, background: '#46A8F9', color: '#fff' }}>
                  {emailResetResendIn > 0
                    ? t('twoFactorEmailReset.sendAgainIn', { time: formatLockTime(emailResetResendIn) })
                    : t('twoFactorEmailReset.sendAgain')}
                </button>
                <button type="button" onClick={leaveEmailResetPanel}
                  className="w-full font-semibold text-sm rounded-full"
                  style={{ height: 44, background: '#fff', color: '#15354F', border: '1.5px solid #E2E8F0' }}>
                  {t('twoFactorEmailReset.backToSignIn')}
                </button>
              </div>
            </div>
          )}
          
          {!emailResetPanel && (
          <BottomActionBar className="-mx-6 [--bar-px:24px]">
            <button type="submit" disabled={loading || smsSending || !!lockTimer}
              className="w-full font-bold text-base text-white rounded-full active:scale-[0.98] transition-transform disabled:opacity-50"
              style={{ height: 50, background: '#46A8F9', letterSpacing: '0.2px' }}>
              {loading || smsSending ? `${t('auth.signIn')}...` : t('auth.signIn')}
            </button>
          </BottomActionBar>
          )}
        </form>
        
        <p className="text-center text-sm mt-5 bottom-actions [--ba-min:24px]" style={{ color: '#64748B' }}>
          {t('auth.noAccount')}{' '}
          <button onClick={() => navigate('/email')} className="font-semibold" style={{ color: '#46A8F9' }}>{t('auth.signUp')}</button>
        </p>
      </div>
    </div>
    <SsoCodeSheet step={sso.codeStep} busy={sso.codeBusy} error={sso.codeError} onSubmit={sso.submitCode} onCancel={sso.cancelCode} />
    </ResponsiveWrapper>
  )
}
